// ============================================================================
// InviteLinkDialog — диалог «Пригласить по ссылке» (роль/срок/лимит → готовая
// ссылка + «Копировать»). Общий для ЗВОНКА В ЧАТЕ и ad-hoc звонка из карточки
// (для ad-hoc цель ссылки резолвится вызывающим — см. note).
// SSoT: lib/chatInvites (обёртки RPC + построение ссылки); семантика — сервер.
// ============================================================================

import { useState } from 'react';
import { Link2, Copy, Check, Loader2 } from 'lucide-react';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToastStore } from '@/store/toastStore';
import { friendlyError, logErrorToJournal } from '@/lib/errors';
import { createConversationInvite, copyInviteLink, INVITE_ROLE_LABELS, type InviteRole } from '@/lib/chatInvites';

interface Props {
  conversationId: string;
  /** Заголовок-подсказка (название чата/объекта). */
  title?: string;
  /** Пояснение для ad-hoc (если ссылка ведёт не в ту же комнату звонка). */
  note?: string;
  onClose: () => void;
}

const ROLES: InviteRole[] = ['participant', 'guest', 'viewer'];

const EXPIRY_OPTIONS: Array<{ label: string; minutes: number }> = [
  { label: '1 час', minutes: 60 },
  { label: '24 часа', minutes: 1440 },
  { label: '7 дней', minutes: 10080 },
  { label: 'Без срока', minutes: 0 },
];

export function InviteLinkDialog({ conversationId, title, note, onClose }: Props) {
  const showError = useToastStore((s) => s.showError);
  const showSuccess = useToastStore((s) => s.showSuccess);
  const [role, setRole] = useState<InviteRole>('participant');
  const [expiryMinutes, setExpiryMinutes] = useState(1440);
  const [maxUses, setMaxUses] = useState(0);
  const [creating, setCreating] = useState(false);
  const [link, setLink] = useState('');
  const [copied, setCopied] = useState(false);

  const create = async () => {
    setCreating(true);
    try {
      const inv = await createConversationInvite(conversationId, {
        roleOnJoin: role,
        expiresInMinutes: expiryMinutes,
        maxUses: Math.max(0, Math.floor(Number(maxUses)) || 0),
      });
      setLink(inv.url);
    } catch (e: unknown) {
      showError(friendlyError(e));
      logErrorToJournal(e, 'не удалось создать ссылку-приглашение');
    } finally {
      setCreating(false);
    }
  };

  const copy = async () => {
    try {
      await copyInviteLink(link);
      setCopied(true);
      showSuccess('Ссылка скопирована');
      window.setTimeout(() => setCopied(false), 2000);
    } catch (e: unknown) {
      showError(friendlyError(e));
      logErrorToJournal(e, 'не удалось скопировать ссылку-приглашение');
    }
  };

  return (
    <UniversalWindow
      open
      onClose={onClose}
      title={title ? `Пригласить по ссылке: ${title}` : 'Пригласить по ссылке'}
      width="max-w-md"
      height="auto"
      resizable={false}
    >
      <div className="space-y-3 p-4">
        {note && (
          <div className="flex gap-2 rounded-md border bg-muted/40 p-2 text-[11px] leading-snug text-muted-foreground">
            <Link2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{note}</span>
          </div>
        )}

        {!link ? (
          <>
            <div>
              <Label className="text-[11px]">Роль при входе</Label>
              <div className="mt-1 grid grid-cols-3 gap-1.5">
                {ROLES.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRole(r)}
                    className={`rounded-full px-2 py-1.5 text-[11px] ${role === r ? 'bg-primary font-medium text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent/40'}`}
                  >
                    {INVITE_ROLE_LABELS[r]}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[11px]">Срок действия</Label>
              <div className="mt-1 grid grid-cols-4 gap-1.5">
                {EXPIRY_OPTIONS.map((o) => (
                  <button
                    key={o.minutes}
                    type="button"
                    onClick={() => setExpiryMinutes(o.minutes)}
                    className={`rounded-full px-2 py-1.5 text-[11px] ${expiryMinutes === o.minutes ? 'bg-primary font-medium text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent/40'}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-[11px]">Лимит использований (0 — без ограничения)</Label>
              <Input
                type="number"
                min={0}
                value={maxUses}
                onChange={(e) => setMaxUses(Number(e.target.value))}
                className="mt-1 h-8 text-xs"
              />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" variant="outline" size="sm" className="h-8 text-[11px]" onClick={onClose}>
                Отмена
              </Button>
              <Button type="button" size="sm" className="h-8 text-[11px]" onClick={() => void create()} disabled={creating}>
                {creating ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Link2 className="mr-1 h-3.5 w-3.5" />}
                Создать ссылку
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="text-[11px] text-muted-foreground leading-snug">
              Ссылка готова. Отправьте её — по ней участник войдёт в чат (роль: {INVITE_ROLE_LABELS[role]}).
            </div>
            <div className="flex items-center gap-1">
              <input
                readOnly
                value={link}
                onFocus={(e) => e.target.select()}
                className="h-8 flex-1 rounded border bg-input px-2 text-[11px] font-mono outline-none"
              />
              <Button type="button" size="sm" variant="outline" className="h-8 shrink-0 text-[11px]" onClick={() => void copy()}>
                {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
                Копировать
              </Button>
            </div>
            <div className="flex justify-end pt-1">
              <Button type="button" size="sm" className="h-8 text-[11px]" onClick={onClose}>
                Готово
              </Button>
            </div>
          </>
        )}
      </div>
    </UniversalWindow>
  );
}
